import { bunja } from 'bunja';
import { atom } from 'jotai';

import { postUserProfileSocialConnectXApi, postUserProfileSocialDisconnectXApi } from '@flipster/apis/v1/user';
import { FormattedMessage } from '@flipster/i18n';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { snackbar } from '@flipster/react/design-system/snackbar/index';
import { TextButton } from '@flipster/react/design-system/text-button';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorCode } from '@flipster/utils/error';
import segment from '@flipster/utils/segment';
import { ProfileEvent } from '@flipster/utils/segment/events/profile';

import { profileBunja } from '../../../../../../bunja';
import { editProfileBunja } from '../../../../bunja';
import * as oauth from '../../../../shared/oauth';
import { getXRedirectUri } from '../../../../shared/oauth';

export const socialXBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { refetchProfile } = bunja.use(profileBunja);
  const { saveDraft, xConnectionAtom, initialSocialProvidersSegmentAtom } = bunja.use(editProfileBunja);
  const isLoadingAtom = atom(false);
  async function connect() {
    segment.track(ProfileEvent.EditProfilePageConnectXClick, store.get(initialSocialProvidersSegmentAtom));
    saveDraft();
    const pk = await oauth.generateAndStorePkceProofKey();
    const url = oauth.generateXOauthUrl(pk);
    window.location.href = url;
  }
  function disconnect() {
    segment.track(ProfileEvent.EditProfilePageDisconnectXClick);
    dialog.open({
      title: {
        label: <FormattedMessage id='Autogen_common.r251126.s_Disconnect_X_account' />,
      },
      content: <FormattedMessage id='Autogen_common.r251126.s_Are_you_sure_you_want_to_disconnect_your_X_account' />,
      buttons: {
        primary: (
          <Dialog.Button
            hierarchy='primary'
            label={<FormattedMessage id='Common.r250401.Confirm' />}
            onClick={async () => {
              segment.track(ProfileEvent.EditProfilePageDisconnectXConfirmClick);
              try {
                await postUserProfileSocialDisconnectXApi();
              } finally {
                snackbar.success({
                  message: <FormattedMessage id='Autogen_common.r251126.s_Your_X_account_is_now_disconnected' />,
                });
                await refetchProfile();
              }
            }}
          />
        ),
        secondary: (
          <Dialog.Button
            hierarchy='secondary'
            color='neutral'
            label={<FormattedMessage id='Common.r250401.Cancel' />}
            onClick={() => segment.track(ProfileEvent.EditProfilePageDisconnectXCancelClick)}
          />
        ),
      },
    });
  }
  bunja.effect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('code')) return;
    (async () => {
      try {
        store.set(isLoadingAtom, true);
        const { code, codeVerifier } = oauth.verifyRedirectCallback();
        await postUserProfileSocialConnectXApi({ code, codeVerifier, redirectUri: getXRedirectUri() });
        segment.track(ProfileEvent.EditProfilePageXConnectionStatus, { is_success: true });
        snackbar.success({
          message: <FormattedMessage id='Autogen_common.r251126.s_Your_X_account_is_now_connected' />,
        });
        await refetchProfile();
      } catch (error) {
        segment.track(ProfileEvent.EditProfilePageXConnectionStatus, { is_success: false });
        const code = getErrorCode(error);
        if (code == 'api.user.profile.social.alreadyConnectedXAccount') {
          return snackbar.error({
            message: (
              <FormattedMessage id='Autogen_common.r251126.s_This_X_account_already_in_use_with_another_Flipster_account' />
            ),
          });
        }
        snackbar.error({
          message: <FormattedMessage id='Autogen_common.r251126.s_Couldn_t_connect_to_X_right_now' />,
          button: <TextButton label={<FormattedMessage id='Autogen_common.r251126.s_Try_again' />} onClick={connect} />,
        });
      } finally {
        store.set(isLoadingAtom, false);
      }
    })();
  });
  return { isLoadingAtom, xConnectionAtom, connect, disconnect };
});
socialXBunja.debugLabel = 'socialXBunja';
